import * as THREE from 'three';
import TWEEN from '@tweenjs/tween.js';
import {USE_LEGACY_TWEEN_GROUP} from 'components/customer/Preview3DCommon/lib/tween';
import {AnimationHandler} from 'components/customer/Preview3D/animation/entity/AnimationHandler';
import {PreviewFormValues} from 'components/customer/Preview3D/types';

type AnimationFn = (animationSpeed?: number) => Promise<void>;

const createRotationAnimation = (
    mesh: THREE.Group<THREE.Object3DEventMap> | THREE.Mesh,
    degrees: number,
    isRightDoor: boolean,
    animationDelay: number
): {open: AnimationFn; close: AnimationFn} => {
    const radians = (degrees * Math.PI) / 180;
    const current = {y: 0};
    const openTarget = {y: isRightDoor ? radians : -radians};
    const closeTarget = {y: 0};

    const open = (animationSpeed = 1000) =>
        new Promise<void>((resolve) => {
            const delay = animationSpeed !== 0 ? animationDelay : 0;
            new TWEEN.Tween(current, USE_LEGACY_TWEEN_GROUP)
                .to(openTarget, animationSpeed)
                .easing(TWEEN.Easing.Quadratic.Out)
                .delay(delay)
                .onComplete(() => resolve())
                .onUpdate(() => {
                    mesh.rotation.y = current.y;
                })
                .start();
        });

    const close = (animationSpeed = 1000) =>
        new Promise<void>((resolve) => {
            new TWEEN.Tween(current, USE_LEGACY_TWEEN_GROUP)
                .to(closeTarget, animationSpeed)
                .easing(TWEEN.Easing.Quadratic.Out)
                .onComplete(() => resolve())
                .onUpdate(() => {
                    mesh.rotation.y = current.y;
                })
                .start();
        });

    return {open, close};
};

const createRepositionYAnimation = (
    mesh: THREE.Group<THREE.Object3DEventMap> | THREE.Mesh,
    depth: number,
    depthOffset: number
): {open: AnimationFn; close: AnimationFn} => {
    const closeTarget = {y: depth};
    const openTarget = {y: depth - depthOffset};
    const current = {y: closeTarget.y};

    const open = (animationSpeed = 1000) =>
        new Promise<void>((resolve) => {
            new TWEEN.Tween(current, USE_LEGACY_TWEEN_GROUP)
                .to(openTarget, animationSpeed)
                .easing(TWEEN.Easing.Quadratic.Out)
                .onComplete(() => resolve())
                .onUpdate(() => {
                    mesh.position.y = current.y;
                })
                .start();
        });

    const close = (animationSpeed = 600) =>
        new Promise<void>((resolve) => {
            const delay = animationSpeed !== 0 ? 600 : 0;
            new TWEEN.Tween(current, USE_LEGACY_TWEEN_GROUP)
                .to(closeTarget, animationSpeed)
                .easing(TWEEN.Easing.Quadratic.Out)
                .delay(delay)
                .onComplete(() => resolve())
                .onUpdate(() => {
                    mesh.position.y = current.y;
                })
                .start();
        });

    return {open, close};
};

const createRepositionY2Animation = (
    mesh: THREE.Group<THREE.Object3DEventMap> | THREE.Mesh,
    depth: number,
    depthOffset: number
): {open: AnimationFn; close: AnimationFn} => {
    const closeTarget = {y: depth - depthOffset};
    const openTarget = {y: depth - 60};
    const current = {y: closeTarget.y};

    const open = (animationSpeed = 1000) =>
        new Promise<void>((resolve) => {
            const delay = animationSpeed !== 0 ? 600 : 0;
            new TWEEN.Tween(current, USE_LEGACY_TWEEN_GROUP)
                .to(openTarget, animationSpeed)
                .easing(TWEEN.Easing.Quadratic.Out)
                .delay(delay)
                .onComplete(() => resolve())
                .onUpdate(() => {
                    mesh.position.y = current.y;
                })
                .start();
        });

    const close = (animationSpeed = 600) => {
        if (animationSpeed === 0) return Promise.resolve();

        return new Promise<void>((resolve) => {
            new TWEEN.Tween(current, USE_LEGACY_TWEEN_GROUP)
                .to(closeTarget, animationSpeed)
                .easing(TWEEN.Easing.Quadratic.Out)
                .onComplete(() => resolve())
                .onUpdate(() => {
                    mesh.position.y = current.y;
                })
                .start();
        });
    };

    return {open, close};
};

const createRepositionXAnimation = (
    mesh: THREE.Group<THREE.Object3DEventMap> | THREE.Mesh,
    openX: number,
    closeX: number,
    animationDelay: number
): {open: AnimationFn; close: AnimationFn} => {
    const closeTarget = {x: closeX};
    const openTarget = {x: openX};
    const current = {x: closeTarget.x};

    const open = (animationSpeed = 1000) =>
        new Promise<void>((resolve) => {
            const delay = animationSpeed !== 0 ? animationDelay : 0;
            new TWEEN.Tween(current, USE_LEGACY_TWEEN_GROUP)
                .to(openTarget, animationSpeed)
                .easing(TWEEN.Easing.Quadratic.Out)
                .delay(delay)
                .onComplete(() => resolve())
                .onUpdate(() => {
                    mesh.position.x = current.x;
                })
                .start();
        });

    const close = (animationSpeed = 600) =>
        new Promise<void>((resolve) => {
            new TWEEN.Tween(current, USE_LEGACY_TWEEN_GROUP)
                .to(closeTarget, animationSpeed)
                .easing(TWEEN.Easing.Quadratic.Out)
                .onComplete(() => resolve())
                .onUpdate(() => {
                    mesh.position.x = current.x;
                })
                .start();
        });

    return {open, close};
};

export const getFoldKitReturnPanelWidth = (values: PreviewFormValues) => {
    const doorLeftMargin = Number(values?.cabinet_door_left) || 0;
    const voidWidth = Boolean(values.cabinet_cover_void)
        ? Number(values.cabinet_void_width) || 0
        : 0;
    return (
        Number(values.cabinet_return_panel_width) + doorLeftMargin - voidWidth
    );
};

export const toggleFoldKitDoorHandler = ({
    mesh,
    isRightDoor = false,
    degrees = 90,
    positionXOffset = 0,
    animationDelay = 300,
    values,
    carcaseThickness = 16.5,
}: {
    mesh: THREE.Group<THREE.Object3DEventMap> | THREE.Mesh;
    isRightDoor?: boolean;
    degrees?: number;
    carcaseThickness?: number;
    positionXOffset?: number;
    animationDelay?: number;
    values?: PreviewFormValues;
}): AnimationHandler => {
    const boundingBox = new THREE.Box3().setFromObject(mesh);
    const size = new THREE.Vector3();
    boundingBox.getSize(size);
    const doorLeftMargin = Number(values?.cabinet_door_left) || 0;
    const doorRightMargin = Number(values?.cabinet_door_right) || 0;
    const voidWidth = Boolean(values.cabinet_cover_void)
        ? Number(values.cabinet_void_width) || 0
        : 0;
    const depthOffset = 240;
    const returnPanelWidth = getFoldKitReturnPanelWidth(values);
    const rightDoorCloseX =
        Number(values.cabinet_width) -
        returnPanelWidth +
        doorLeftMargin +
        carcaseThickness -
        doorRightMargin;

    if (!isRightDoor) {
        mesh.position.x += positionXOffset;
        mesh.scale.x = -1;
    }

    const rotation = createRotationAnimation(
        mesh,
        degrees,
        isRightDoor,
        animationDelay
    );
    const repositionY = createRepositionYAnimation(
        mesh,
        mesh.position.y,
        depthOffset
    );
    const repositionY2 = createRepositionY2Animation(
        mesh,
        mesh.position.y,
        depthOffset
    );
    const repositionX = createRepositionXAnimation(
        mesh,
        isRightDoor
            ? returnPanelWidth / 2 + size.x + voidWidth
            : returnPanelWidth / 2 - voidWidth,
        isRightDoor ? rightDoorCloseX : returnPanelWidth,
        animationDelay
    );

    return {
        open: [
            rotation.open,
            repositionY.open,
            repositionX.open,
            repositionY2.open,
        ],
        close: [
            rotation.close,
            repositionY.close,
            repositionX.close,
            repositionY2.close,
        ],
    };
};
